import React, { useId, useMemo, useRef, useState, type Ref } from "react";
import classNames from "classnames";
import { CalendarIcon } from "@icons";
import { Button } from "@design-system/Button/Button";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@design-system/Popover/Popover";
import { useSeededOpen } from "@design-system/hooks/useSeededOpen";
import { mergeRefs } from "@design-system/overlay/mergeRefs";
import type { DisableableTrigger } from "@design-system/overlay/triggerButtonProps";
import type {
    DatePickerButtonProps,
    DatePickerFieldControlProps,
    DatePickerPreset,
    DatePickerSelectionMode,
    DatePickerTriggerElement,
    DatePickerVariant,
} from "./DatePicker.types";
import { DatePickerModeSwitcher } from "./DatePickerModeSwitcher";
import { DatePickerPanelInput } from "./DatePickerPanelInput";
import { DatePickerPresets } from "./DatePickerPresets";
import { DatePickerFooter } from "./DatePickerFooter";
import { DatePickerTriggerInput } from "./DatePickerTriggerInput";
import { useDatePickerBlur } from "./useDatePickerBlur";
import styles from "./DatePicker.module.scss";

interface DatePickerShellProps<TValue> {
    value: TValue;
    onValueChange: (value: TValue, presetId?: string) => void;
    onBlur?: () => void;
    name?: string;
    /** Hidden-input value submitted under `name`. */
    serializeValue: (value: TValue) => string;
    ref?: Ref<DatePickerTriggerElement>;
    invalid?: boolean;
    isEqual: (first: TValue, second: TValue) => boolean;
    formatValue: (value: TValue) => string;
    /**
     * Formats the value using the raw input format (e.g. `MM/dd/yyyy`)
     * rather than the display format. Shown while the panel input is
     * focused so what you see matches what `parseValue` expects; the
     * friendlier `formatValue` output is restored on blur.
     */
    formatValueForEditing: (value: TValue) => string;
    parseValue: (value: string) => TValue | undefined;
    renderCalendar: (
        value: TValue,
        onValueChange: (value: TValue) => void,
        close: () => void,
    ) => React.ReactNode;
    emptyValue: TValue;
    todayValue: TValue;
    presets: ReadonlyArray<DatePickerPreset<TValue>>;
    selectionMode: DatePickerSelectionMode;
    availableModes?: readonly DatePickerSelectionMode[];
    onModeChange?: (mode: DatePickerSelectionMode) => void;
    variant: DatePickerVariant;
    id?: string;
    label?: string;
    placeholder: string;
    triggerLabel?: string;
    popupLabel?: string;
    portalContainer?: HTMLElement;
    trigger?: DisableableTrigger;
    buttonProps?: DatePickerButtonProps;
    fieldControlProps?: DatePickerFieldControlProps;
    disabled?: boolean;
    required?: boolean;
    clearable?: boolean;
    showActions?: boolean;
    showTodayButton?: boolean;
    showPresets?: boolean;
    showTriggerIcon?: boolean;
    pinnablePresets?: boolean;
    defaultPinnedPresetIds?: readonly string[];
    onPinnedPresetIdsChange?: (pinnedPresetIds: readonly string[]) => void;
    className?: string;
    open?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    inputFormat: string | readonly string[];
    selectedPresetId?: string | null;
}

export const DatePickerShell = <TValue,>({
    value,
    onValueChange,
    onBlur,
    name,
    serializeValue,
    ref,
    invalid = false,
    isEqual,
    formatValue,
    formatValueForEditing,
    parseValue,
    renderCalendar,
    emptyValue,
    todayValue,
    presets,
    selectionMode,
    availableModes = [selectionMode],
    onModeChange,
    variant,
    id,
    label,
    placeholder,
    triggerLabel,
    popupLabel,
    portalContainer,
    trigger,
    buttonProps,
    fieldControlProps,
    disabled = false,
    required = false,
    clearable = true,
    showActions = false,
    showTodayButton = false,
    showPresets = false,
    showTriggerIcon = true,
    pinnablePresets = false,
    defaultPinnedPresetIds,
    onPinnedPresetIdsChange,
    className,
    open,
    onOpenChange,
    inputFormat,
    selectedPresetId,
}: DatePickerShellProps<TValue>) => {
    const generatedId = useId();
    const inputId = fieldControlProps?.id ?? id ?? generatedId;
    const ariaInvalid = invalid || fieldControlProps?.["aria-invalid"];
    const ariaRequired =
        fieldControlProps?.["aria-required"] ?? (required || undefined);
    const triggerRef = useMemo(
        () => mergeRefs<DatePickerTriggerElement>([ref]),
        [ref],
    );
    const { rootRef, popupRef, handleBlur } = useDatePickerBlur(onBlur);
    const [pinnedPresetIds, setPinnedPresetIds] = useState(
        () => new Set(defaultPinnedPresetIds ?? []),
    );
    const shouldKeepPopoverOpenRef = useRef(false);
    const [draftState, setDraftState] = useState({
        mode: selectionMode,
        value,
    });
    const { isOpen, setOpen } = useSeededOpen({
        open,
        onOpenChange,
        initialSeededOpen: open ?? false,
        onSeed: () => setDraftState({ mode: selectionMode, value }),
    });
    const isInline = variant === "inline" || variant === "embedded";
    const draftValue =
        draftState.mode === selectionMode ? draftState.value : value;
    // While the panel is closed, always reflect the live `value` prop rather
    // than the draft: the draft is only seeded when the panel opens (see
    // `useSeededOpen` above) or on mount, so an externally-updated `value` (e.g. an
    // async fetch resolving after mount) would otherwise never reach the
    // closed trigger's label until the user opened the panel once.
    const activeValue = showActions && isOpen ? draftValue : value;
    const showClear =
        clearable && !required && !isEqual(activeValue, emptyValue);
    const formattedValue = formatValue(activeValue);
    const editableValue = formatValueForEditing(activeValue);
    const handlePopoverOpenChange: React.ComponentProps<
        typeof Popover
    >["onOpenChange"] = (nextIsOpen, eventDetails) => {
        if (!nextIsOpen && shouldKeepPopoverOpenRef.current) {
            shouldKeepPopoverOpenRef.current = false;
            eventDetails.cancel();
            return;
        }
        setOpen(nextIsOpen);
    };
    const handleModeChange = (nextMode: DatePickerSelectionMode) => {
        if (!onModeChange) {
            return;
        }
        shouldKeepPopoverOpenRef.current = true;
        onModeChange(nextMode);
        queueMicrotask(() => {
            shouldKeepPopoverOpenRef.current = false;
        });
    };
    const selectValue = (nextValue: TValue, presetId?: string) => {
        setDraftState({ mode: selectionMode, value: nextValue });
        if (!showActions) {
            onValueChange(nextValue, presetId);
        }
    };
    const togglePresetPin = (presetId: string) => {
        const nextPinnedPresetIds = new Set(pinnedPresetIds);
        if (nextPinnedPresetIds.has(presetId)) {
            nextPinnedPresetIds.delete(presetId);
        } else {
            nextPinnedPresetIds.add(presetId);
        }
        setPinnedPresetIds(nextPinnedPresetIds);
        onPinnedPresetIdsChange?.(Array.from(nextPinnedPresetIds));
    };
    const panelInput = (
        <DatePickerPanelInput
            inputId={inputId}
            label={label}
            formattedValue={formattedValue}
            editableValue={editableValue}
            placeholder={placeholder}
            disabled={disabled}
            inputFormat={inputFormat}
            parseValue={parseValue}
            emptyValue={emptyValue}
            onValueChange={selectValue}
        />
    );
    const hiddenInput = name !== undefined && (
        <input
            type="hidden"
            name={name}
            value={serializeValue(value)}
            disabled={disabled}
        />
    );

    const panel = (
        <div
            className={classNames(styles.panel, {
                [styles.embeddedPanel]: variant === "embedded",
                [styles.withPresets]: showPresets,
            })}
        >
            {availableModes.length > 1 && (
                <DatePickerModeSwitcher
                    mode={selectionMode}
                    availableModes={availableModes}
                    onModeChange={handleModeChange}
                />
            )}
            {variant === "panel-input" && panelInput}
            <div className={styles.panelBody}>
                {showPresets && (
                    <DatePickerPresets
                        presets={presets}
                        activeValue={activeValue}
                        isEqual={isEqual}
                        selectedPresetId={selectedPresetId}
                        onSelect={(nextValue, presetId) => {
                            selectValue(nextValue, presetId);
                            if (!showActions && !isInline) {
                                setOpen(false);
                            }
                        }}
                        pinnedPresetIds={
                            pinnablePresets || pinnedPresetIds.size > 0
                                ? pinnedPresetIds
                                : undefined
                        }
                        onTogglePin={
                            pinnablePresets ? togglePresetPin : undefined
                        }
                    />
                )}
                <div className={styles.calendarContainer}>
                    {renderCalendar(activeValue, selectValue, () =>
                        setOpen(false),
                    )}
                </div>
            </div>
            {(showActions || showTodayButton || showClear) && (
                <DatePickerFooter
                    showActions={showActions}
                    showTodayButton={showTodayButton}
                    showClear={showClear}
                    onToday={() => selectValue(todayValue)}
                    onClear={() => {
                        selectValue(emptyValue);
                        if (!showActions && !isInline) {
                            setOpen(false);
                        }
                    }}
                    onCancel={() => {
                        setDraftState({ mode: selectionMode, value });
                        if (!isInline) {
                            setOpen(false);
                        }
                    }}
                    onApply={() => {
                        onValueChange(draftValue);
                        if (!isInline) {
                            setOpen(false);
                        }
                    }}
                />
            )}
        </div>
    );

    if (isInline) {
        return (
            <div
                ref={rootRef}
                className={classNames(styles.root, className)}
                onBlur={handleBlur}
            >
                {hiddenInput}
                {variant === "inline" && label && (
                    <div className={styles.inlineLabel}>{label}</div>
                )}
                {variant === "inline" && panelInput}
                {panel}
            </div>
        );
    }

    const buttonTriggerValue =
        formattedValue.length > 0
            ? formattedValue
            : (triggerLabel ?? placeholder);
    const triggerValue =
        variant === "button" || variant === "panel-input"
            ? buttonTriggerValue
            : formattedValue;
    const isButtonTrigger = variant === "button" || variant === "panel-input";
    const defaultTrigger = isButtonTrigger ? (
        <Button
            type="button"
            variant="primary"
            size="md"
            {...buttonProps}
            ref={triggerRef}
            id={fieldControlProps?.id ?? buttonProps?.id}
            aria-labelledby={
                fieldControlProps?.["aria-labelledby"] ??
                buttonProps?.["aria-labelledby"]
            }
            aria-describedby={fieldControlProps?.["aria-describedby"]}
            aria-invalid={ariaInvalid}
            aria-required={ariaRequired}
            disabled={disabled || buttonProps?.disabled}
            aria-label={buttonProps?.["aria-label"] ?? label}
            leftIcon={showTriggerIcon ? <CalendarIcon /> : undefined}
            className={classNames(
                { [styles.blockTrigger]: buttonProps?.block },
                buttonProps?.className,
            )}
        >
            {triggerValue}
        </Button>
    ) : (
        <div
            className={classNames(styles.inputTrigger, {
                [styles.disabledTrigger]: disabled,
            })}
        >
            <DatePickerTriggerInput
                ref={triggerRef}
                inputId={inputId}
                label={label}
                formattedValue={formattedValue}
                editableValue={editableValue}
                placeholder={placeholder}
                disabled={disabled}
                showTriggerIcon={showTriggerIcon}
                inputFormat={inputFormat}
                parseValue={parseValue}
                emptyValue={emptyValue}
                onValueChange={selectValue}
                fieldControlProps={fieldControlProps}
                ariaInvalid={ariaInvalid}
                ariaRequired={ariaRequired}
            />
        </div>
    );

    return (
        <div
            ref={rootRef}
            className={classNames(styles.root, className)}
            onBlur={handleBlur}
        >
            {hiddenInput}
            <Popover open={isOpen} onOpenChange={handlePopoverOpenChange}>
                <PopoverTrigger disabled={disabled}>
                    {trigger
                        ? React.cloneElement(trigger, { disabled })
                        : defaultTrigger}
                </PopoverTrigger>
                <PopoverContent
                    ref={popupRef}
                    className={styles.popover}
                    align="start"
                    disableAnchorTracking
                    portalContainer={portalContainer}
                    aria-label={popupLabel ?? label ?? "Choose a date"}
                >
                    {panel}
                </PopoverContent>
            </Popover>
        </div>
    );
};
